import { z } from 'zod'

// Login Request Schema
export const LoginRequestSchema = z.object({
  username: z.string().min(3, 'Username must be at least 3 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters')
})

// User Schema
export const UserSchema = z.object({
  id: z.number().int().positive(),
  username: z.string(),
  email: z.string().email().optional(),
  role: z.enum(['admin', 'user', 'manager', 'employee']),
  created_at: z.string().datetime().optional()
})

// Login Response Schema
export const LoginResponseSchema = z.object({
  token: z.string().uuid('Token must be a valid UUID'),
  user: UserSchema.pick({ id: true, username: true, role: true })
})

// Token Validation Schema
export const TokenSchema = z.string().uuid('Invalid token format')

// Logout Request Schema
export const LogoutRequestSchema = z.object({
  token: z.string().uuid().optional()
})

// Logout Response Schema - NEW
export const LogoutResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional()
})

// Token Refresh Request Schema - NEW
export const TokenRefreshRequestSchema = z.object({
  token: z.string().uuid('Current token must be a valid UUID')
})

// Token Refresh Response Schema - NEW  
export const TokenRefreshResponseSchema = z.object({
  token: z.string().uuid('New token must be a valid UUID'),
  expires_at: z.string().datetime().optional()
})

// User Profile Request Schema - NEW
export const UserProfileRequestSchema = z.object({
  // No body needed - uses auth token
})

// User Profile Response Schema - NEW
export const UserProfileResponseSchema = z.object({
  user: UserSchema
})

// Auth Headers Schema for validating Authorization header
export const AuthHeaderSchema = z.object({
  authorization: z.string().regex(/^Bearer [0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'Authorization header must be in format: Bearer <uuid>')
})

// Query parameter schema for authenticated requests
export const AuthQuerySchema = z.object({
  token: z.string().uuid().optional()
})

// Export TypeScript types
export type LoginRequest = z.infer<typeof LoginRequestSchema>
export type LoginResponse = z.infer<typeof LoginResponseSchema>
export type User = z.infer<typeof UserSchema>
export type Token = z.infer<typeof TokenSchema>
export type LogoutRequest = z.infer<typeof LogoutRequestSchema>
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>
export type TokenRefreshRequest = z.infer<typeof TokenRefreshRequestSchema>
export type TokenRefreshResponse = z.infer<typeof TokenRefreshResponseSchema>
export type UserProfileRequest = z.infer<typeof UserProfileRequestSchema>
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>
export type AuthHeader = z.infer<typeof AuthHeaderSchema>
export type AuthQuery = z.infer<typeof AuthQuerySchema>

// Helper function to extract token from Authorization header
export const extractTokenFromHeader = (authHeader: string): string | null => {
  const match = authHeader.match(/^Bearer (.+)$/)
  return match ? match[1] : null
}

// Helper function to validate token format
export const isValidTokenFormat = (token: string): boolean => {
  const result = TokenSchema.safeParse(token)
  return result.success
} 